import { FeatureFlagOption } from "@herbjoy/apex-api-models";
import { ILogService, IPromise, IQService } from "angular";
import get from "lodash/get";
import set from "lodash/set";
import { SiteFeatureService } from "../services/data-services/site-feature.service";
import { InstanceDb } from "../services/instance-db.service";
import { FeatureFlagOptions } from "./feature-flag-options";
import { SessionService } from "../services/session.service";

type FeaturePrefs = { [key: string]: FeatureFlagOption };

/**
 * A local store used to persist feature preferences for the session.
 */
export class FeatureManager {
    featurePrefs: FeaturePrefs | null;
    isSync = false;
    featuresLoaded: IPromise<FeaturePrefs>;
    isAuthorized = false;

    constructor(private $log: ILogService, private $q: IQService, private sessionService: SessionService,
        private siteFeatureService: SiteFeatureService, private instanceDb: InstanceDb) {
        'ngInject';
        sessionService.isAuthorized().then(isAuthorized => {
            this.isAuthorized = isAuthorized;
        });
    }

    /**
     * Return whether the feature is enabled in the app.
     * @param feature The feature key name
     */
    isEnabled(feature: string) {
        const option = this.getFeatureFlagOption(feature);
        //TODO: A/B-testing logic when option is noPref.
        return option === FeatureFlagOptions.enabled || option === FeatureFlagOptions.optIn;
    }
    /**
    * Saves the beta opt in feature at server side
    * @param optIn A boolean indicating if beta payroll entry is opted In
    */
    saveFeatureFlagOption(optIn: boolean) {
        return this.siteFeatureService.saveBetaParticipation(optIn)
    }
    /**
    * Saves the beta opt in feature at server side
    * @param optIn A boolean indicating if beta payroll entry is opted In
    */
    getFeatureFlagOptionApi() {
        return this.siteFeatureService.getBetaParticipation();
    }
    /**
     * Return the feature flag option for the given feature.
     * @param feature The feature key name.
     */
    getFeatureFlagOption(feature: keyof FeaturePrefs) {
        if (!this.featurePrefs) {
            // Return undefined to indicate no data is available
            this.$log.warn(`Attempted to get feature preference for "${feature}",`
                + ` but the feature prefs were not loaded.`);
            return;
        }
        // Use lodash to get the feature flag by string chain expression.
        // If the full preferences are known and have no value set, default to No Preference.
        return get(this.featurePrefs, feature, FeatureFlagOptions.noPref);
    }

    /**
     * Return the list of all feature flags along with their settings.
     */
    getFeatureFlags() {
        return this.featurePrefs;
    }

    /**
     * Opt the user into the feature with the given name.
     * @param feature The feature key name.
     */
    optIn(feature: string) {
        const featurePref = this.getFeatureFlagOption(feature);
        if (featurePref === FeatureFlagOptions.enabled
            || featurePref === FeatureFlagOptions.disabled) {
            // If the feature is forcibly enabled or disabled, settings preferences is not possible.
            return featurePref;
        }
        set(this.featurePrefs, feature, FeatureFlagOptions.optIn)
        this._saveLocalPreferences();
    }

    /**
     * Opt the user out of the feature with the given name.
     * @param feature The feature key name.
     */
    optOut(feature: string) {
        const featurePref = this.getFeatureFlagOption(feature);
        if (featurePref === FeatureFlagOptions.enabled
            || featurePref === FeatureFlagOptions.disabled) {
            // If the feature is forcibly enabled or disabled, settings preferences is not possible.
            return featurePref;
        }
        set(this.featurePrefs, feature, FeatureFlagOptions.optOut);
        this._saveLocalPreferences();
    }

    ensureLoaded(): IPromise<FeatureManager> {
        if (this.isSync) {
            return this.$q.resolve(this);
        }
        this.featuresLoaded = this.featuresLoaded || this._loadServerFeatures().then(() => this.featurePrefs);
        return this.featuresLoaded.then(() => this);
    }

    _loadServerFeatures() {
        const localPrefs = this.instanceDb.sessionStorage.getItem("apexFeaturePrefStore");
        if (localPrefs) {
            this.featurePrefs = JSON.parse(localPrefs);
            this.isSync = true;
            // Continue to load the remote features in order to purge stale flags.
        }
        return this.siteFeatureService.retrieveFeatures()
            .then(features => {
                // Get the feature preferences that are forcibly enabled
                // or disabled at the server level.
                this.featurePrefs = this._mergePrefs(this.featurePrefs || {}, features)
                // Indicate that the services is loaded and safe to use synchronously.
                this.isSync = true;
                if (this.isAuthorized) {
                    if (!localPrefs) {
                        this.getFeatureFlagOptionApi().then(data => {
                            if (data) {
                                this.featurePrefs.payroll['v2PayrollEntry'] = "optIn";
                            } else {
                                this.featurePrefs.payroll['v2PayrollEntry'] = "optOut";
                            }
                            this._saveLocalPreferences();
                        });
                    } else {
                        this._saveLocalPreferences();
                    }
                }
            });
    }

    _saveLocalPreferences() {
        this.instanceDb.sessionStorage.setItem("apexFeaturePrefStore", JSON.stringify(this.featurePrefs));
    }

    _mergePrefs(localPrefs, remotePrefs) {
        for (const featureFlag of remotePrefs) {
            const remoteValue = featureFlag.option;
            const localValue = get(localPrefs, featureFlag.feature);
            if (remoteValue === "enabled" || remoteValue === "disabled" || !localValue
                || localValue === FeatureFlagOptions.noPref) {
                // Enabled and disabled features can never be overridden. Also, if the local pref
                // has no value or is marked as having no preference, use whatever value is specified.
                set(localPrefs, featureFlag.feature, remoteValue);
            }
        }
        return localPrefs
    }
}
